import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { filter, firstValueFrom } from 'rxjs';
import { ChildWindowBuilderService, WindowTarget } from './embedding/child.service';
import { AuthenticationService } from './authentication/authentication.service';
import { SissCloudTokenService } from '../sign/siss-cloud/siss-cloud-sso';
import { RadioSettingsService } from './settings/radio-settings.service';
import {
    DedalusPrescriptionNreResponse,
    ICommunityPrescriptionRequest,
    InteractivePrescriptionResponse,
    TokenResponse,
} from '../models/community-prescription.models';
import { CommunityPrescriptionSettings, Examination, NREType } from 'estensa-module';
import { ServerRootService } from '../utils/routing/serverroot.service';
import { VisitExamination } from '../models/visit';
import { EcgExamination, LightEcgExam } from '../models/ecg';
import { ListDataSourceService } from './list-datasource.service';
import { ISourceItemDataExtended } from '@se/models';
import { getJsonObjectNestedProp, JsonObject } from '@se/cp.common';

export type ExamDataForCommunityPrescription = {
    localId: string;
    exam: { otherCode: string; guid: string; externalCode: string };
    patient: { id: string; firstName: string; lastName: string; otherPatientId: string; patientId: string };
};

@Injectable({
    providedIn: 'root',
})
export class CommunityPrescriptionService extends ServerRootService {
    test = false; //usato per simulare endpoint terze parti con serve in locale
    accessToken = '';

    static examinationToExamDataForCommunityPrescription(examination: Examination): ExamDataForCommunityPrescription {
        return {
            localId: examination.localId,
            exam: { otherCode: examination.exam.otherCode, guid: examination.exam.id, externalCode: '' },
            patient: {
                id: examination.patient.id,
                firstName: examination.patient.firstName,
                lastName: examination.patient.lastName,
                otherPatientId: examination.patient.otherPatientId || '',
                patientId: Reflect.get(examination.patient, 'patientId') || examination.patient.otherPatientId,
            },
        };
    }

    static reportExaminationToExamDataForCommunityPrescription(report: JsonObject): ExamDataForCommunityPrescription {
        return {
            localId: String(getJsonObjectNestedProp(report, ['ReferenceExamination', 'LocalId']) || ''),
            exam: {
                otherCode: String(
                    getJsonObjectNestedProp(report, ['ReferenceExamination', 'IdExam', 'OtherCode']) || '',
                ),
                guid: String(getJsonObjectNestedProp(report, ['ReferenceExamination', 'IdExam', 'Id']) || ''),
                externalCode: String(
                    getJsonObjectNestedProp(report, ['ReferenceExamination', 'IdExam', 'ExternalCode']) || '',
                ),
            },
            patient: {
                id: String(getJsonObjectNestedProp(report, ['Patient', 'Id']) || ''),
                firstName: String(getJsonObjectNestedProp(report, ['Patient', 'FirstName']) || ''),
                lastName: String(getJsonObjectNestedProp(report, ['Patient', 'LastName']) || ''),
                otherPatientId: String(getJsonObjectNestedProp(report, ['Patient', 'OtherPatientId']) || ''),
                patientId: String(getJsonObjectNestedProp(report, ['Patient', 'OtherPatientId']) || ''),
            },
        };
    }

    static visitExaminationToExamDataForCommunityPrescription(
        examination: VisitExamination,
    ): ExamDataForCommunityPrescription {
        return {
            localId: examination.localId,
            exam: { otherCode: examination.exam.otherCode || '', guid: examination.exam.id, externalCode: '' },
            patient: {
                id: examination.patient.id,
                firstName: examination.patient.firstName || '',
                lastName: examination.patient.lastName || '',
                otherPatientId: examination.patient.otherPatientId || '',
                patientId: examination.patient.patientId || '',
            },
        };
    }

    static ecgExaminationToExamDataForCommunityPrescription(
        examination: EcgExamination,
    ): ExamDataForCommunityPrescription {
        return {
            localId: examination.id,
            exam: { otherCode: examination.exam.id || '', guid: examination.exam.id, externalCode: '' },
            patient: {
                id: examination.patient.id,
                firstName: examination.patient.firstName || '',
                lastName: examination.patient.lastName || '',
                otherPatientId: Reflect.get(examination.patient, 'otherPatientId') || '',
                patientId: examination.patient.patientId || '',
            },
        };
    }

    static ecgExaminationToExamDataFromCommunityPrescription(exam: LightEcgExam): ExamDataForCommunityPrescription {
        return {
            localId: exam?.examinationId || '',
            exam: {
                otherCode: exam?.examinationId || '',
                guid: exam?.reportId || '',
                externalCode: '',
            },
            patient: {
                id: exam?.patient?.id || '',
                firstName: exam?.patient?.firstName || '',
                lastName: exam?.patient?.lastName || '',
                otherPatientId: Reflect.get(exam?.patient || {}, 'otherPatientId') || '',
                patientId:
                    Reflect.get(exam?.patient || {}, 'patientId') ||
                    Reflect.get(exam?.patient || {}, 'otherPatientId') ||
                    '',
            },
        };
    }

    lsExam: ISourceItemDataExtended[] = [];

    // constructor
    constructor(
        private httpClient: HttpClient,
        private childWindow: ChildWindowBuilderService,
        private sissCloudTokenService: SissCloudTokenService,
        private auth: AuthenticationService,
        private radioSettingsService: RadioSettingsService,
        private listDataSourceService: ListDataSourceService,
    ) {
        super();
    }

    getServerRoot() {
        return this.test ? 'test' : super.getServerRoot();
    }

    protected getHeader(contentType: string): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': contentType,
            Authorization: `Bearer ${this.accessToken}`,
        });
    }

    getCommunityPrescriptionSettings(): CommunityPrescriptionSettings | undefined {
        if (this.test)
            return {
                radio: true,
                cardio: true,
                RT: true,
                url: 'http://localhost:43210/community-prescription/interactive/EXT_TOKEN_EXT',
                tokenUrl: 'http://localhost:43210/community-prescription/token',
                NREUrl: 'http://localhost:43210/community-prescription/nre/<EXT_TOKEN_EXT>',
                applicationCode: 'SUITESTENSA',
                NREType: NREType.SISSWay,
            };

        return this.radioSettingsService.radioSettings?.communityPrescriptionSettings;
    }

    isCommunityPrescriptionEnabled(): boolean {
        const settings = this.getCommunityPrescriptionSettings();
        return Boolean(settings?.radio || settings?.cardio || settings?.RT);
    }
    /**
     *
     * @param uri nei settings http://any/path?token=<EXT_TOKEN_EXT>
     * @param token ricevuto come risposta all prima chiamata a dedalus
     */
    prepareCommunityPrescriptionUriWithToken(uri: string, token: string): string {
        return uri.replace('<EXT_TOKEN_EXT>', token).replace('EXT_TOKEN_EXT', token);
    }

    async openCommunityPrescriptionWindow(
        url: string,
        communityPrescriptionToken: string,
    ): Promise<InteractivePrescriptionResponse> {
        const windowWidth = window.innerWidth < 1280 ? window.innerWidth : 1280;
        const windowHeight = window.innerHeight < 768 ? window.innerHeight : 768;
        const uriWithToken = this.prepareCommunityPrescriptionUriWithToken(url, communityPrescriptionToken);
        const serviceWindow = await this.childWindow
            .withUrl(uriWithToken)
            .withTarget(WindowTarget.Blank)
            .withSize(windowWidth, windowHeight)
            .open();

        return new Promise<InteractivePrescriptionResponse>((resolve, reject) => {
            let dataResponse: InteractivePrescriptionResponse = {
                idPrescription: '',
                token: '',
            };
            const messageListener = (event: MessageEvent) => {
                if (event.source === serviceWindow.window) {
                    try {
                        console.log(
                            '%c Community Prescription Response:',
                            'color: #007bff; font-weight: bold;',
                            'receiving data in community prescription service',
                            event.data,
                        );
                        const data = event.data;
                        if (data.idPrescription && data.token) {
                            window.removeEventListener('message', messageListener);
                            dataResponse = {
                                idPrescription: data.idPrescription,
                                token: data.token,
                            };
                            resolve(dataResponse);
                        }
                    } catch (error: any) {
                        console.error('Error parsing message:', event.data, 'error: ', error);
                        reject(error);
                    }
                }
            };

            window.addEventListener('message', messageListener);

            firstValueFrom(serviceWindow.closed$().pipe(filter((closed) => closed)))
                .then(() => {
                    window.removeEventListener('message', messageListener);
                    resolve(dataResponse);
                })
                .catch(reject);
        });
    }

    prepareCommunityPrescriptionBody(
        examinations: ExamDataForCommunityPrescription[],
        applicationCode: string = 'SUITESTENSA',
        tokenSISS: string = this.accessToken,
        isInteractiveMode = false,
    ): ICommunityPrescriptionRequest {
        let username = this.auth.getUserName();
        if (typeof username === 'string') {
            username = username.split('/')[1] || username;
        }
        return {
            isInteractiveMode,
            authentication: {
                applicationId: applicationCode,
                tokenSISS,
                username,
            },
            contact: {
                // order: { se ha valori vuoti da noia a dedalus quindi non si usa piu
                //     placerOrderId: '', //non obbligatorio loro non hanno questi dati quindi non se ne fanno nulla
                // },
                prescriber: {
                    username,
                    //taxCode: '', rimosso perche dava noia....
                },
                patient: {
                    //dicono che firstname e lastname arrivano invertiti...quindi inverto e proviamo
                    firstName: examinations[0].patient.lastName,
                    lastName: examinations[0].patient.firstName,
                    patientExternalId: examinations[0].patient.patientId,
                },
                prescriptions: [
                    {
                        patientType: 'SSN',
                        prescriptionForm: 'RUR09',
                        prescriptionMode: 'ELECTRONIC_WITH_REMINDER',
                        prescriptionType: 'P',
                        priority: 'D',
                        examinations: examinations.map((examination) => ({
                            quantity: 1,
                            //placerOrderNumber: '', /rimosso perche vuoto dava noia.....//non obbligatorio loro non hanno questi dati quindi non se ne fanno nulla
                            examination: {
                                codExamination: examination.exam.externalCode, //codifica dell'esame descrizione della prestazione. lista lsCardioPerform --> otherCode
                            },
                        })),
                    },
                ],
            },
        };
    }

    getCommunityPrescriptionToken(
        uri: string,
        examinations: ExamDataForCommunityPrescription[],
        applicationCode: string,
        tokenSISS: string = this.accessToken,
        isInteractiveMode = false,
    ): Promise<TokenResponse> {
        const requestBody = this.prepareCommunityPrescriptionBody(
            examinations,
            applicationCode,
            tokenSISS,
            isInteractiveMode,
        );
        const options = this.buildOptions();
        try {
            return firstValueFrom(
                this.httpClient.post<TokenResponse>(uri, requestBody, { ...options, observe: 'body' }),
            ) as unknown as Promise<TokenResponse>;
        } catch (err: any) {
            console.error(err.message);
            throw new Error('Error getting community prescription token' + err.message);
        }
    }

    getCommunityPrescriptionNRE(nreUrl: string, token: string) {
        const options = this.buildOptions();
        const uriWithToken = this.prepareCommunityPrescriptionUriWithToken(nreUrl, token);
        try {
            return firstValueFrom(this.httpClient.get<DedalusPrescriptionNreResponse>(uriWithToken, options));
        } catch (err: any) {
            console.error(err.message);
            throw new Error('Error getting community prescription NRE' + err.message);
        }
    }

    private async addExternalCode(examinations: ExamDataForCommunityPrescription[]) {
        if (!examinations.length) return examinations;
        this.lsExam = await this.listDataSourceService.getLSExam();
        const exam = this.lsExam.find((item) => item.guid === examinations[0].exam.guid);

        if (!exam || !exam.externalCode) {
            console.error('%c Missing external code:', 'color: red; font-weight: bold;', { exam, examinations });
            throw new Error('Missing externalCode for examination');
        }

        examinations.forEach((e) => {
            e.exam.externalCode = exam?.externalCode || '';
        });

        console.log('%c Community Prescription Exam:', 'color: #007bff; font-weight: bold;', {
            exam,
            examinations,
            lsExam: this.lsExam,
        });
        return examinations;
    }

    async handleCommunityMedicalPrescriptionInteractive(examinations: ExamDataForCommunityPrescription[]) {
        const communityPrescriptionSettings = this.getCommunityPrescriptionSettings();

        if (!communityPrescriptionSettings) {
            console.error('%c Missing communityPrescriptionSettings:', 'color: red; font-weight: bold;');
            throw new Error('missing communityPrescriptionSettings');
        }

        console.log('%c Community Prescription Settings:', 'color: #007bff; font-weight: bold;', {
            communityPrescriptionSettings,
        });

        const examinationsWithoutExternalCode = examinations.filter((e) => !e.exam.externalCode);
        await this.addExternalCode(examinationsWithoutExternalCode);

        const userId = this.auth.getUserId();
        let sissWayToken = await this.sissCloudTokenService.getFullTokenData(userId);

        const { url, tokenUrl, NREUrl, applicationCode } = communityPrescriptionSettings;

        const communityPrescriptionToken = await this.getCommunityPrescriptionToken(
            tokenUrl,
            examinations,
            applicationCode,
            window.btoa(JSON.stringify(sissWayToken)),
            true,
        );

        console.log('%c Community Prescription Token Response:', 'color: #007bff; font-weight: bold;', {
            communityPrescriptionToken,
        });

        //openContextWindow
        const windowMessageResponse = await this.openCommunityPrescriptionWindow(url, communityPrescriptionToken.token);
        console.log('%c Community Prescription Interactive Response:', 'color: #007bff; font-weight: bold;', {
            windowMessageResponse,
        });

        return await this.getCommunityPrescriptionNRE(NREUrl, communityPrescriptionToken.token);
    }

    async handleCommunityMedicalPrescriptionVisitPatient(examinations: ExamDataForCommunityPrescription[]) {
        const communityPrescriptionSettings = this.getCommunityPrescriptionSettings();
        console.log('%c Community Prescription Settings:', 'color: #007bff; font-weight: bold;', {
            communityPrescriptionSettings,
        });
        if (!communityPrescriptionSettings) {
            console.error('%c Missing communityPrescriptionSettings:', 'color: red; font-weight: bold;');
            throw new Error('missing communityPrescriptionSettings');
        }

        await this.addExternalCode(examinations);

        const userId = this.auth.getUserId();
        const sissWayToken = await this.sissCloudTokenService.getAccessToken(userId);

        const { url, tokenUrl, NREUrl, applicationCode } = communityPrescriptionSettings;

        /*get token in modalita non interattiva*/
        const communityPrescriptionToken = await this.getCommunityPrescriptionToken(
            tokenUrl,
            examinations,
            applicationCode,
            sissWayToken,
        );

        console.log('%c Community Prescription Token Response:', 'color: #007bff; font-weight: bold;', {
            communityPrescriptionToken,
        });

        return await this.getCommunityPrescriptionNRE(NREUrl, communityPrescriptionToken.token);
    }
}
